import React, { SetStateAction, useEffect, useState } from 'react';

import type {
  LoginUserDTO,
  AuthDTO,
  TokenExchangeDTO,
  ApiResponse_string,
} from '../../services/apiClient';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import GoogleLoginButton from '../../components/auth/GoogleLoginButton';
import AuthLayout from '../../components/layout/AuthLayout';
import { GOOGLE_CLIENT_ID } from '../../config/config';
import { useAuthApi } from '../../hooks/useAuthApi';
import { getFirstAvailableRoute } from '../../utils/routePermissions';
import { getPermissions } from '../../utils/tokenUtils';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [showTwoFactorInput, setShowTwoFactorInput] = useState<boolean>(false);
  const [tempAccessToken, setTempAccessToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { generateToken, generateTokenTwoFactor, googleTokenExchange } = useAuthApi();



  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const userPermissions = getPermissions() || [];
      const landingPage = getFirstAvailableRoute(userPermissions);
      navigate(landingPage);
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    const loginData: LoginUserDTO = { email, password };

    try {
      setLoading(true);
      const response = await generateToken(loginData);
      setLoading(false);
      if (response) {
        handleSignInResponse(
          response,
          'Invalid credentials. Please try again.',
        );
      }
    } catch (error: unknown) {
      setLoading(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  const handleTwoFactorVerification = async (
    e: React.FormEvent,
  ): Promise<void> => {
    e.preventDefault();

    try {
      if (!tempAccessToken) {
        setErrorMessage('Session expired. Please log in again.');

        return;
      }

      const response = await generateTokenTwoFactor({
        code: twoFactorCode,
        tempToken: tempAccessToken,
        email,
      });

      if (response && response.success && response.data) {
        const responseData = response.data as AuthDTO;

        // Save the final tokens
        localStorage.setItem('token', responseData.accessToken ?? '');
        localStorage.setItem('refresh_token', responseData.refreshToken ?? '');
        setTempAccessToken(null);
        handleSignInResponse(response, 'Unable to sign in.');
      } else {
        setErrorMessage(
          'Invalid two-factor authentication code. Please try again.',
        );
      }
    } catch (error) {
      console.error('Two factor auth verification error:', error);
      setErrorMessage(
        'Failed to verify two-factor authentication. Please try again.',
      );
    }
  };

  const handleGoogleSignIn = async (token: TokenExchangeDTO): Promise<void> => {
    try {
      setLoading(true);
      const response = await googleTokenExchange(token);
      setLoading(false);

      if (response) {
        handleSignInResponse(response, 'Unable to sign in with Google.');
      }
    } catch (error: unknown) {
      setLoading(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };

  const handleSignInResponse = (
    response: ApiResponse_string,
    errorMessage: SetStateAction<string | null>,
  ): void => {
    if (response && response.success && response.data) {
      setErrorMessage('');

      const responseData = response.data as AuthDTO;

      if (responseData.twoFactorEnabled) {
        // 2FA is enabled, show the 2FA input field
        setTempAccessToken(responseData.accessToken ?? '');
        setShowTwoFactorInput(true);
      } else {
        // 2FA is not enabled, proceed with login
        localStorage.setItem('token', responseData.accessToken ?? '');
        localStorage.setItem('refresh_token', responseData.refreshToken ?? '');
        
        // Get user permissions and find first available route
        const userPermissions = getPermissions() || [];
        const landingPage = getFirstAvailableRoute(userPermissions);
        
        navigate(landingPage);
      }
    } else {
      setErrorMessage(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div>
        <h2 className="font-['Archivo'] text-[28px] font-black text-text mb-1">Sign In</h2>
        <p className="cg-label mb-7">// Access your admin portal</p>

        {errorMessage && (
          <p className="text-[#f87171] font-['Space_Mono'] text-[10px] mb-4">{errorMessage}</p>
        )}

        {!showTwoFactorInput ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="cg-label block mb-[7px]">Email Address</label>
              <input
                id="email"
                type="email"
                className="cg-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@example.com"
              />
            </div>
            <div className="mb-5">
              <div className="flex justify-between items-center mb-[7px]">
                <label className="cg-label block">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-accent font-['Space_Mono'] text-[10px] underline decoration-[rgba(146,222,113,0.3)]"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                className="cg-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your Password"
              />
            </div>
            <button type="submit" className="cg-btn cg-btn-primary w-full h-11" disabled={loading}>
              {loading ? 'Loading...' : 'Sign In →'}
            </button>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-line" />
              <span className="cg-tag">or</span>
              <div className="flex-1 h-px bg-line" />
            </div>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLoginButton
                handleGoogleSignIn={handleGoogleSignIn}
                setErrorMessage={setErrorMessage}
              />
            </GoogleOAuthProvider>
          </form>
        ) : (
          <form onSubmit={handleTwoFactorVerification}>
            <div className="mb-5">
              <p className="font-['Archivo'] text-[13px] text-text mb-4">
                Enter the 2FA code from your authentication app.
              </p>
              <label className="cg-label block mb-[7px]">Authentication Code</label>
              <input
                id="twoFactorCode"
                type="text"
                className="cg-input"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                maxLength={6}
                placeholder="6-digit code"
              />
            </div>
            <button type="submit" className="cg-btn cg-btn-primary w-full h-11" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify →'}
            </button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

export default Login;
